import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import type {
    PaginationState,
    RowSelectionState,
    SortingState
} from '@tanstack/react-table';
import { ColumnDef } from '@tanstack/table-core';
import UnifiedTable from '@enerex/unified-ui-react/Table';

import SearchTable from './SearchTable';
import DateColumn from './DateColumn';
import LinkColumn from './LinkColumn';
import AppliedFilters from './AppliedFilters';

import { getTablePaginationSizes } from 'utils/get-table-pagination-sizes';
import { noop } from 'utils/index';
import { ApiFilters } from 'types/api-inputs.type';
import { DrawerContentProps } from 'components/Drawer';

type TableProps<TableValues> = {
    columns: ColumnDef<TableValues, any>[];
    data: TableValues[];
    loading?: boolean;
    enableSearch?: boolean;
    enablePagination?: boolean;
    enableHideColumns?: boolean;
    enableSorting?: boolean;
    enableFilters?: boolean;
    enableExport?: boolean;
    enableSelection?: boolean;
    enablePaginateAll?: boolean;
    total?: number;
    pageSizes?: number[];
    hook?: () => ApiFilters<TableValues>;
    filters?: (
        props: DrawerContentProps &
            Pick<ApiFilters<TableValues>, 'filters' | 'setFilters' | 'extra'>
    ) => React.ReactElement;
    hiddenColumns?: string[];
    onExport?: () => void;
    onRowSelected?: (selectedRows: TableValues[]) => void;
    defaultAllSelected?: boolean;
    noDataMessage?: string;
    searchPlaceholder?: string;
    extra?: any;
};

function noopHook<TableValues>(): ApiFilters<TableValues> {
    return {
        ascendingOrder: false,
        limit: 10,
        orderBy: undefined,
        page: 0,
        search: '',
        setAscendingOrder: noop,
        setLimit: noop,
        setOrderBy: noop,
        setPage: noop,
        setSearch: noop,
        filters: null,
        setFilters: noop,
        extra: null
    };
}

const Table = <TableValues extends object = Record<string, never>>({
    columns,
    data = [],
    searchPlaceholder = undefined,
    loading = false,
    enableSearch = true,
    enablePagination = true,
    enableHideColumns = true,
    enableSorting = true,
    enableFilters = true,
    enableSelection = false,
    enablePaginateAll = true,
    pageSizes = [],
    hook = noopHook,
    filters: Filters = undefined,
    onExport = noop,
    enableExport = false,
    onRowSelected,
    defaultAllSelected = false,
    total = 0,
    extra = null,
    noDataMessage = 'No data found'
}: TableProps<TableValues>) => {
    const {
        limit,
        setLimit,
        page,
        setPage,
        search,
        setSearch,
        orderBy,
        setOrderBy,
        ascendingOrder,
        setAscendingOrder,
        filters,
        setFilters
    } = hook();

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const pagination = useMemo<PaginationState>(
        () => ({
            pageIndex: page,
            pageSize: limit
        }),
        [limit, page]
    );

    const sorting = useMemo<SortingState>(
        () => (orderBy ? [{ id: String(orderBy), desc: !ascendingOrder }] : []),
        [ascendingOrder, orderBy]
    );

    const globalFilter = useMemo(
        () => ({
            ...(filters || {}),
            search
        }),
        [filters, search]
    );

    const sizePerPageList = useMemo(() => {
        const sizes = getTablePaginationSizes(
            total,
            enablePaginateAll,
            ...pageSizes
        ).map((size) => size.value);

        return Array.from(new Set(sizes.filter((size) => size > 0)));
    }, [enablePaginateAll, pageSizes, total]);

    const onPaginationChange = useCallback(
        (
            updater:
                | PaginationState
                | ((old: PaginationState) => PaginationState)
        ) => {
            const next =
                typeof updater === 'function' ? updater(pagination) : updater;

            if (next.pageSize !== limit) {
                setLimit(next.pageSize);
            }
            if (next.pageIndex !== page) {
                setPage(next.pageIndex);
            }
        },
        [limit, page, pagination, setLimit, setPage]
    );

    const onSortingChange = useCallback(
        (updater: SortingState | ((old: SortingState) => SortingState)) => {
            const next =
                typeof updater === 'function' ? updater(sorting) : updater;
            const sort = next[0];

            setOrderBy(sort?.id as keyof TableValues);
            setAscendingOrder(sort ? !sort.desc : false);
        },
        [setAscendingOrder, setOrderBy, sorting]
    );

    const onGlobalFilterChange = useCallback(
        (updater: any) => {
            const next =
                typeof updater === 'function'
                    ? updater(globalFilter)
                    : updater || {};
            const { search: nextSearch = '', ...nextFilters } = next;

            setSearch(nextSearch);
            setFilters(nextFilters);
        },
        [globalFilter, setFilters, setSearch]
    );

    const onClose = useCallback(
        (data) => {
            extra?.onApply?.(data);
        },
        [extra]
    );

    const FilterComponent = useMemo(() => {
        if (!Filters) {
            return undefined;
        }

        return ({ hide }: { hide: () => void }) => (
            <Filters
                hide={hide}
                filters={filters}
                setFilters={setFilters}
                extra={extra}
            />
        );
    }, [Filters, extra, filters, setFilters]);

    const AppliedFiltersComponent = useCallback(
        () => <AppliedFilters hook={hook} filterClose={onClose} />,
        [hook, onClose]
    );

    const customHeader = useMemo(() => {
        if (!enableSearch && !enableExport) {
            return null;
        }

        return (
            <div className="d-flex align-items-center justify-content-between gap-2 w-100">
                <div style={{ minWidth: enableSearch ? 260 : 0 }}>
                    {enableSearch && (
                        <SearchTable
                            search={search}
                            setSearch={setSearch}
                            placeholder={searchPlaceholder}
                            buttonDisabled={loading}
                            setPage={setPage}
                        />
                    )}
                </div>
                {enableExport && (
                    <Button variant="secondary" onClick={onExport}>
                        Export
                    </Button>
                )}
            </div>
        );
    }, [
        enableExport,
        enableSearch,
        loading,
        onExport,
        search,
        searchPlaceholder,
        setPage,
        setSearch
    ]);

    useEffect(() => {
        if (enableSelection && onRowSelected) {
            const selectedRows = Object.keys(rowSelection)
                .filter((key) => rowSelection[key])
                .map((key) => data[Number(key)])
                .filter(Boolean);

            onRowSelected(selectedRows);
        }
    }, [data, enableSelection, onRowSelected, rowSelection]);

    useEffect(() => {
        if (enableSelection && defaultAllSelected && data?.length > 0) {
            setRowSelection(
                data.reduce<RowSelectionState>((selection, _row, index) => {
                    selection[index] = true;
                    return selection;
                }, {})
            );
        }
    }, [data, defaultAllSelected, enableSelection]);

    return (
        <UnifiedTable<TableValues>
            columns={columns}
            data={data}
            loading={loading}
            pageSizes={sizePerPageList}
            enableSearch={false}
            enablePagination={enablePagination}
            enableHideColumns={enableHideColumns}
            enableSorting={enableSorting}
            enableFilters={enableFilters}
            enableSelection={enableSelection}
            enablePaginateAll={false}
            enableColumnVisibilityStorage={false}
            enableGlobalFilterStorage={false}
            enableUrlPagination={false}
            pagination={pagination}
            onPaginationChange={onPaginationChange}
            sorting={sorting}
            onSortingChange={onSortingChange}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            globalFilter={globalFilter}
            onGlobalFilterChange={onGlobalFilterChange}
            filterComponent={FilterComponent}
            appliedFilters={AppliedFiltersComponent}
            total={total}
            noDataMessage={noDataMessage}
            customHeader={customHeader}
        />
    );
};

export { DateColumn, LinkColumn };

export default Table;
